/**
 * Lighting classification — the ONE answer to "what kind of light is this?"
 *
 * A light's kind is spread over three fields that were each introduced for a
 * different reason, and NONE of them is a taxonomy:
 *
 *   - `proceduralStyle: 'recessed'` says how to RENDER it (the procedural can
 *     instead of a GLB). It is also part of the placeholder-vs-product
 *     contract (`PROCEDURAL_ONLY_FIELDS` in shared-lib/fixture-types).
 *   - `lightingSubtype: 'pendant' | 'chandelier'` is the CATALOG discriminator
 *     the Design Center queries Firestore by (`catalogSubtype`).
 *   - `modelId` says which catalog product it is, and the drum flush mount is
 *     recognised by its id alone.
 *
 * Collapsing them into one stored field would touch the catalog query
 * contract, the procedural/product write-boundary invariant, the iOS Design
 * Picker mirror and every saved structure. So the fields stay as they are and
 * the READING is centralised here instead: anything that needs to know what a
 * light is asks this module, and a future migration changes one function.
 *
 * Pure: no React, no canvas, no Firestore.
 */

/** The drum flush-mount GLB — recognised by id, it carries no subtype. */
const DRUM_LIGHT_MODEL_ID = 'iLQqplu3sfDJR21dFGHy';

const typeOf = (obj) =>
  typeof obj?.type === 'string' ? obj.type.toLowerCase() : '';

/** True for anything that hangs from / sits on a ceiling as a light. */
const isCeilingLight = (obj) =>
  typeOf(obj) === 'ceiling-light' || obj?.category?.['ceiling-light'] === true;

/** True for a wall-mounted light (sconce, vanity bar, picture light). */
const isWallLight = (obj) => {
  const type = typeOf(obj);
  return (
    type === 'wall-light' ||
    type === 'walllight' ||
    type === 'overhead-wall-light' ||
    type === 'sconce' ||
    obj?.category?.['wall-light'] === true
  );
};

/**
 * True for the 4" procedural can. This is the one distinction the electrical
 * drawing draws differently, and the one the ceiling-light editor re-lays out,
 * so both read it from here.
 */
const isRecessedCan = (obj) => obj?.proceduralStyle === 'recessed';

/**
 * True for a ceiling light that HANGS on a stem or cord — a pendant or a
 * chandelier, as opposed to a can or a flush dome sitting in the ceiling.
 *
 * The distinction earns its own predicate because hanging is what makes a
 * fixture belong in a wall ELEVATION: it comes down off the ceiling into the
 * drawing, over the counter whose elevation is being drawn, and its hang
 * height is a number a builder sets. A flush fixture is the ceiling.
 *
 * Both subtypes are the catalog's own discriminator (`catalogSubtype`), so
 * this reads the field rather than guessing from geometry.
 */
const isHungCeilingLight = (obj) => {
  if (!isCeilingLight(obj) || isRecessedCan(obj)) return false;
  const subtype = obj?.lightingSubtype;
  return subtype === 'pendant' || subtype === 'chandelier';
};

/**
 * The electrical-plan symbol a light is drawn with, or null when the object is
 * not a light.
 *
 *   'recessed' — concentric circles: the downlight can.
 *   'surface'  — circle with four radiating ticks: EVERY other ceiling
 *                fixture. A pendant, a chandelier, a drum and a puck are all
 *                one outlet to an electrician; the fixture they hang is a
 *                schedule entry, not a different symbol.
 *   'sconce'   — circle on a stem out of the wall.
 *
 * @param {Object} obj
 * @returns {'recessed'|'surface'|'sconce'|null}
 */
const lightSymbolOf = (obj) => {
  if (!obj) return null;
  if (isWallLight(obj)) return 'sconce';
  if (!isCeilingLight(obj)) return null;
  return isRecessedCan(obj) ? 'recessed' : 'surface';
};

module.exports = {
  DRUM_LIGHT_MODEL_ID,
  isCeilingLight,
  isWallLight,
  isRecessedCan,
  isHungCeilingLight,
  lightSymbolOf,
};
